import { toast as baseToast, clearToasts, Toaster, type ToasterToast } from '@planera/ui-components/feedback';
import { css } from '@styled/css';

import { AUTOMATION_IDS } from 'state_management/automationIds';
export { type ToasterToast, type ToastType } from '@planera/ui-components/feedback';
export { clearToasts };

/** HTML attributes for toast, excluding 'title' which conflicts with ToasterToast.title */
type ToastHTMLAttributes = Omit<React.HTMLAttributes<HTMLLIElement>, 'title'> & { [key: `data-${string}`]: string | undefined };

/** Toast props extended with native HTML attributes (data-*, aria-*, etc.) */
type ToastProps = Omit<ToasterToast, 'id' | 'open' | 'type'> & ToastHTMLAttributes;

/** Wrapper that accepts HTML attributes and passes them through to the toast */
export function toast(props: Omit<ToasterToast, 'id' | 'open'> & ToastHTMLAttributes) {
  return baseToast(props as Parameters<typeof baseToast>[0]);
}

function normalizeProps(messageOrProps: React.ReactNode | ToastProps): ToastProps {
  // If it's a plain object with a 'title' property, treat it as ToastProps
  if (messageOrProps !== null && typeof messageOrProps === 'object' && 'title' in messageOrProps) {
    return messageOrProps;
  }
  // Otherwise treat it as the message/title
  return { title: messageOrProps as React.ReactNode };
}

/** Shows a toast without an icon. Pass a message string or full props object. */
export function showToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.DEFAULT, ...normalizeProps(messageOrProps), type: 'default' });
}

/** Shows a success toast. Pass a message string or full props object. */
export function showSuccessToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.SUCCESS, ...normalizeProps(messageOrProps), type: 'success' });
}

/** Shows an error toast. Pass a message string or full props object. */
export function showErrorToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.ERROR, ...normalizeProps(messageOrProps), type: 'error' });
}

/** Shows an info toast. Pass a message string or full props object. */
export function showInfoToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.INFO, ...normalizeProps(messageOrProps), type: 'info' });
}

/** Shows a warning toast. Pass a message string or full props object. */
export function showWarningToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.WARNING, ...normalizeProps(messageOrProps), type: 'warning' });
}

/** Shows a loading toast with spinner. Pass a message string or full props object. */
export function showLoadingToast(messageOrProps: React.ReactNode | ToastProps) {
  return toast({ 'data-testid': AUTOMATION_IDS.TOAST.LOADING, ...normalizeProps(messageOrProps), type: 'loading' });
}

/** Dismisses all visible toasts. */
export const dismissToast = clearToasts;

/** ToastsProvider - Wraps the app with toast context and renders toast containers. */
export function ToastsProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        limit={3}
        position="top"
        viewportClassName={css({ pt: '3.25rem' })}
      />
    </>
  );
}
